<script>
import { MapboxMap, MapboxNavigationControl } from '@studiometa/vue-mapbox-gl'
import { backUrl, mapboxPublicKey } from '@src/config'
import { useIndexStore } from '@src/store'
import { FLAT_LINE_WIDTH, FLAT_LINE_COLOR, ITINERARY_LINE_COLOR } from '@src/constants'
import TrafficControl from '@comp/map/TrafficControl'
import api from '@src/api'
import polyline from '@mapbox/polyline'
import MapboxDraw from '@mapbox/mapbox-gl-draw'
import defaultDrawStyles from '@src/default-styles'
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'

export default {
  name: 'TrafficMap',
  components: {
    MapboxMap,
    MapboxNavigationControl,
  },
  props: {
    lines: {
      type: Array,
      required: true,
    },
    currentProject: {
      type: Object,
      default: () => {},
    },
  },
  emits: [
    'newLinesCreated',
  ],
  data () {
    return {
      map: null,
      mapboxPublicKey,
      draw: null,
      linesData: [],
      currentLine: null,
      unsavedModification: false,
    }
  },
  computed: {
    mapStyle () {
      return 'mapbox://styles/mapbox/light-v9'
    },
    store () {
      return useIndexStore()
    },
  },
  watch: {
    currentLine () {
      this.$emit('newLinesCreated', this.linesData)
    },
    currentProject: {
      async handler() {
        this.map.setStyle(this.mapStyle)
        if (this.currentProject) {
          if (this.currentProject.address_center?.length) {
            this.flyToCenter(this.currentProject.address_center)
          }
          await this.tryAutomaticZoom()
        } else {
          this.unzoom()
        }
      },
    },
    lines: {
      handler() {
        if (this.linesData.length > this.lines.length) {
          const lineToDelete = this.linesData.find(line => !this.lines.includes(line))
          this.removeRealItineraries(lineToDelete.segments)
          this.removeLayersAndSource(lineToDelete.draw_id)
        }
        this.linesData = [...this.lines]
        this.changeLineNames()
      },
      deep: true,
    },
  },
  mounted () {
    this.store.changeRoute(this.$options.name)
  },
  methods: {
    exitEditMode () { // called by MapPage.vue
      this.draw.changeMode('simple_select')
    },
    getUniqueCoordinates (segments) {
      const coordinates = []
      const existingCoordinates = new Set()
      for (const segment of segments) {
        const [latFrom, lngFrom] = segment.from.split(',').map(Number)
        const [latTo, lngTo] = segment.to.split(',').map(Number)
        const keyFrom = `${lngFrom},${latFrom}`
        if (!existingCoordinates.has(keyFrom)) {
          coordinates.push([lngFrom, latFrom])
          existingCoordinates.add(keyFrom)
        }
        const keyTo = `${lngTo},${latTo}`
        if (!existingCoordinates.has(keyTo)) {
          coordinates.push([lngTo, latTo])
          existingCoordinates.add(keyTo)
        }
      }
      return coordinates
    },
    editLine (line) { // called by MapPage.vue
      this.removeRealItineraries(line.segments)
      this.removeLayersAndSource(line.draw_id)
      const geojson = {
        type: 'Feature',
        properties: { name: line.name },
        geometry: {
          type: 'LineString',
          coordinates: this.getUniqueCoordinates(line.segments),
        },
      }
      const lineFeatureId = this.draw.add(geojson)[0]
      this.draw.changeMode('direct_select', { featureId: lineFeatureId })
      this.currentLine = line
    },
    unzoom () {
      this.map.flyTo({ zoom: 0 })
    },
    flyToCenter (center) {
      this.map.flyTo({
        center,
        zoom: 11,
      })
    },
    async delay (ms = 100) {
      await new Promise(resolve => setTimeout(resolve, ms))
    },
    async tryAutomaticZoom (retries = 10) {
      if (this.lines.length) {
        this.automaticZoom()
      } else if (retries > 0) {
        await this.delay(100)
        await this.tryAutomaticZoom(retries - 1)
      }
    },
    automaticZoom () {
      const bounds = this.retrieveBounds()
      const padding = 100
      this.map.fitBounds(bounds, { padding })
    },
    retrieveBounds () {
      const allPoints = []
      const segments = this.lines.map(line => line.segments)
      for (const segment of segments) {
        for (const points of segment) {
          allPoints.push(points.from)
          allPoints.push(points.to)
        }
      }
      const latitudes = []
      const longitudes = []
      const allPointsWithoutDuplicates = [...new Set(allPoints)]
      allPointsWithoutDuplicates.forEach(pt => {
        const [lat, lon] = pt.split(',').map(Number)
        latitudes.push(lat)
        longitudes.push(lon)
      })
      const minLongitude = Math.min(...longitudes)
      const maxLongitude = Math.max(...longitudes)
      const minLatitude = Math.min(...latitudes)
      const maxLatitude = Math.max(...latitudes)
      return [[maxLongitude, maxLatitude], [minLongitude, minLatitude]]
    },
    changeLineNames () {
      for (const lineData of this.linesData) {
        if (this.map.getLayer(lineData.draw_id)) {
          this.map.setLayoutProperty(lineData.draw_id, 'text-field', lineData.name)
        } else {
          const coordinates = []
          for (const segment of lineData.segments) {
            const fromPoint = segment.from.split(',').map(Number)
            const toPoint = segment.to.split(',').map(Number)
            coordinates.push(/* long, lat order */
              [fromPoint[1], fromPoint[0]],
              [toPoint[1], toPoint[0]],
            )
          }
          this.drawRealItineraries(lineData.segments)
          this.drawFlatItineraries(lineData, coordinates)
        }
      }
    },
    onMapLoaded (event) {
      this.map = event
      this.map.on('load', this.initializeMap)
    },
    async onDrawCreate (event) {
      const coordinates = event.features[0].geometry.coordinates
      if (coordinates.length >= 1) {
        let lineName = this.$gettext('New line')
        if (event.line_name) {
          lineName = event.line_name
        }
        const uniqueID = Date.now().toString(36) + Math.random().toString(36).substring(2)
        const newLine = {
          id: -1,
          draw_id: uniqueID,
          name: lineName,
          segments: [],
        }
        for (let i = 0; i < coordinates.length; i++) {
          if (coordinates[i + 1]) {
            const segment = {
              from: coordinates[i][1] + ',' + coordinates[i][0],
              to: coordinates[i + 1][1] + ',' + coordinates[i + 1][0],
              polyline: '',
              distance: -1,
              distance_text: '',
              average_duration: -1,
              average_duration_text: '',
              start_address: '',
              end_address: '',
            }
            newLine.segments.push(segment)
          }
        }
        this.drawFlatItineraries(newLine, coordinates)
        this.linesData.push(newLine)
        await this.getDirections(newLine)
        this.currentLine = newLine
      }
    },
    async onDrawUpdate (event) {
      if (!this.currentLine) {
        return
      }
      const drawId = this.currentLine.draw_id
      if (this.currentLine.segments.length) {
        this.removeRealItineraries(this.currentLine.segments)
      }
      this.removeLayersAndSource(drawId)
      const index = this.linesData.findIndex(line => line.draw_id === drawId)
      if (index !== -1) {
        this.linesData.splice(index, 1)
      }
      event.line_name = this.currentLine.name
      await this.onDrawCreate(event)
    },
    removeLayersAndSource (drawId) {
      if (this.map.getLayer(`stroke${drawId}`)) {
        this.map.removeLayer(`stroke${drawId}`)
      }
      if (this.map.getLayer(drawId)) {
        this.map.removeLayer(drawId)
      }
      if (this.map.getSource(drawId)) {
        this.map.removeSource(drawId)
      }
    },
    removeRealItineraries (segments) {
      for (const segment of segments) {
        const polyId = segment.polyline.points
        if (polyId) {
          if (this.map.getLayer(polyId)) {
            this.map.removeLayer(polyId)
          }
          if (this.map.getSource(polyId)) {
            this.map.removeSource(polyId)
          }
        }
      }
    },
    drawFlatItineraries (lineData, coordinates) {
      const routeGeoJSON = {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: coordinates,
        },
      }
      this.map.addSource(lineData.draw_id, {
        type: 'geojson',
        data: routeGeoJSON,
      })
      this.map.addLayer({
        id: `stroke${lineData.draw_id}`,
        type: 'line',
        source: lineData.draw_id,
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-dasharray': [0.2, 2],
          'line-color': FLAT_LINE_COLOR,
          'line-width': FLAT_LINE_WIDTH,
        },
      })
      this.map.addLayer({
        id: lineData.draw_id,
        type: 'symbol',
        source: lineData.draw_id,
        layout: {
          'symbol-placement': 'line',
          'text-font': ['Open Sans Regular'],
          'text-field': lineData.name,
          'text-size': 32,
        },
      })
    },
    drawRealItineraries (segments) {
      for (const segment of segments) {
        const encodedPolyline = segment.polyline.points
        const routeCoordinates = polyline.decode(encodedPolyline)
        const reversedCoordinates = routeCoordinates.map(([lon, lat]) => [lat, lon])
        const routeGeoJSON = {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: reversedCoordinates,
          },
        }
        this.map.addSource(encodedPolyline, {
          type: 'geojson',
          data: routeGeoJSON,
        })
        this.map.addLayer({
          id: encodedPolyline,
          type: 'line',
          source: encodedPolyline,
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
          },
          paint: {
            'line-color': ITINERARY_LINE_COLOR,
            'line-width': FLAT_LINE_WIDTH,
          },
        })
      }
    },
    async getDirections (line) {
      const promises = []
      for (let i = 0; i < line.segments.length; i++) {
        const from = line.segments[i].from
        const to = line.segments[i].to
        const url = `${backUrl}directions/directions_orig_dest?orig=${from}&dest=${to}`
        promises.push(api.get(url))
      }
      return Promise.all(promises).then(values => {
        values.forEach((value, index) => {
          if (value.status === 'OK') {
            const segment = line.segments[index]
            const route = value.routes[0]
            const leg = route.legs[0]
            segment.polyline = route.overview_polyline
            segment.distance = leg.distance.value
            segment.distance_text = leg.distance.text
            segment.average_duration = leg.duration.value
            segment.average_duration_text = leg.duration.text
            segment.start_address = leg.start_address
            segment.end_address = leg.end_address
          }
        })
        this.drawRealItineraries(line.segments)
      }).catch(err => {
        this.store.changeNotification({
          text: err,
          autoClose: false,
          type: 'error',
        })
      })
    },
    initializeMap () {
      this.draw = new MapboxDraw({
        displayControlsDefault: false,
        controls: {
          line_string: true,
          trash: true,
        },
        defaultMode: 'simple_select',
        styles: defaultDrawStyles,
      })
      this.map.addControl(new TrafficControl(), 'bottom-right')
      this.map.addControl(this.draw, 'top-right')
      this.map.on('draw.create', this.onDrawCreate)
      this.map.on('draw.update', this.onDrawUpdate)
      this.linesData = [...this.lines]
      this.changeLineNames()
    },
  },
}
</script>
<template>
  <MapboxMap
    style="width: calc(100% - 50px); height: calc(100% - 50px); position:absolute;"
    :access-token="mapboxPublicKey"
    :map-style="mapStyle"
    @mb-created="onMapLoaded"
  >
    <MapboxNavigationControl position="bottom-right" />
  </MapboxMap>
</template>
